import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, FileText, Loader2, RefreshCw, Upload, X } from "lucide-react";
import {
  FileDropzone,
  UploadQueue,
  toQueueItems,
  useCreateDocuments,
  useFileUpload,
  useIndexingQueue,
  useLinkDocumentsToProject,
  useMetadataQueue,
  type FileRejection,
  type MetadataJobInput,
  type UploadItem,
  type UploadedFile,
} from "@lumanity/ui/ingestion";
import {
  EvidenceFormFields,
  defaultEvidenceFormFields,
  extractedToFormValues,
  resolveFormFields,
  resolveOwnership,
  type EvidenceFormValues,
  type ExtractedMetadata,
  type ProjectCustomField,
} from "@lumanity/ui/evidence";
import { Button, ErrorBanner } from "@lumanity/ui/primitives";
import {
  ACCEPTED_FILE_TYPES,
  PROJECT_OWNERSHIP,
  PROJECT_OWNERSHIP_CONFIG,
  UPLOAD_PATH,
  blankValues,
  duplicateNames,
  extractionStatus,
  filledCustomValues,
  isAllowedDocumentFile,
  isSettled,
  newRow,
  stubDocument,
  toCreatePayload,
  type UploadRow,
} from "./uploadModel";

/**
 * Upload → AI metadata extraction → review → save → index → link to the
 * project, then `onComplete` (FRaiME's DocumentUploadForm order: indexing is
 * enqueued here and runs in the background). Built on
 * @lumanity/ui/ingestion (Recipes → 3); this component only owns the order of
 * the steps. Trimmed from FRaiME's DocumentUploadForm: the duplicate check is a
 * warning on the row, and extractions still running after a reload are resumed.
 */

type SubmitStage = "idle" | "saving" | "indexing" | "linking";

const OWNERSHIP = resolveOwnership(PROJECT_OWNERSHIP_CONFIG);
const FORM_FIELDS = resolveFormFields(defaultEvidenceFormFields(), OWNERSHIP);

export interface DocumentUploadPanelProps {
  projectId: string;
  projectModel?: string;
  customFields: ProjectCustomField[];
  /** Filenames already in the project, for the duplicate warning. */
  existingFilenames: string[];
  /** Every document saved, queued for indexing and added to the project. */
  onComplete: () => void;
  /** Leave upload mode (only offered when the project already has documents). */
  onCancel?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function RowForm({
  row,
  customFields,
  register,
}: {
  row: UploadRow;
  customFields: ProjectCustomField[];
  register: (key: string, form: UseFormReturn<EvidenceFormValues> | null) => void;
}) {
  const form = useForm<EvidenceFormValues>({ defaultValues: blankValues(customFields) });
  const doc = useMemo(() => stubDocument(row), [row]);

  useEffect(() => {
    register(row.key, form);
    return () => register(row.key, null);
  }, [register, row.key, form]);

  return (
    <EvidenceFormFields fields={FORM_FIELDS} form={form} doc={doc} ownership={OWNERSHIP} customFieldDefs={customFields} idPrefix={`upload-${row.key}`} />
  );
}

const DocumentUploadPanel = ({ projectId, projectModel, customFields, existingFilenames, onComplete, onCancel, onDirtyChange }: Readonly<DocumentUploadPanelProps>) => {
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [uploaded, setUploaded] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitStage, setSubmitStage] = useState<SubmitStage>("idle");

  const forms = useRef(new Map<string, UseFormReturn<EvidenceFormValues>>());
  const applied = useRef(new Set<string>());
  // Bumped when a row's form mounts, so a result that arrived first is still applied.
  const [formsVersion, setFormsVersion] = useState(0);
  const registerForm = useCallback((key: string, form: UseFormReturn<EvidenceFormValues> | null) => {
    if (form) forms.current.set(key, form);
    else forms.current.delete(key);
    setFormsVersion((v) => v + 1);
  }, []);

  const onUploadSettled = useRef<(items: UploadItem<UploadedFile[]>[]) => void>();
  const upload = useFileUpload<UploadedFile[]>({ path: UPLOAD_PATH, onSettled: (items) => onUploadSettled.current?.(items) });
  const queue = useMetadataQueue<ExtractedMetadata>();
  const createDocuments = useCreateDocuments();
  const linkDocuments = useLinkDocumentsToProject();
  const indexing = useIndexingQueue();

  useEffect(() => {
    onDirtyChange?.(rows.length > 0);
  }, [rows.length, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const duplicates = useMemo(() => duplicateNames(rows, existingFilenames), [rows, existingFilenames]);
  const statusOf = (row: UploadRow) => extractionStatus(row, queue.documentStatuses);

  // Fill each form once, when its extraction finishes. The user's edits win where the model found nothing.
  useEffect(() => {
    rows.forEach((row) => {
      if (!row.extractionId || applied.current.has(row.extractionId)) return;
      const info = queue.documentStatuses[row.extractionId];
      const form = forms.current.get(row.key);
      if (info?.status !== "Done" || !info.result || !form) return;
      applied.current.add(row.extractionId);
      form.reset({ ...extractedToFormValues(info.result, form.getValues(), customFields), ownership: PROJECT_OWNERSHIP });
    });
  }, [queue.documentStatuses, rows, customFields, formsVersion]);

  // Resume extractions still running for this project (e.g. after a reload).
  useEffect(() => {
    queue
      .fetchPendingJobs(projectId)
      .then((pending) => {
        if (!pending.jobs.length) return;
        const restored: UploadRow[] = pending.jobs.map((job, i) => ({
          key: `pending-${job.document_id}-${i}`,
          name: job.original_name ?? job.filename,
          size: 0,
          uploadedFilename: job.filename,
          downloadUrl: job.download_url ?? "",
          extractionId: job.document_id,
        }));
        setRows(restored);
        setUploaded(true);
        setExpandedKey(restored[0].key);
        queue.startPolling(restored.map((r) => r.extractionId as string));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const enqueueExtraction = async (toExtract: UploadRow[], all: UploadRow[]) => {
    if (toExtract.length === 0) return;
    const jobs: MetadataJobInput[] = toExtract.map((row) => ({
      document_id: row.extractionId ?? crypto.randomUUID(),
      filename: row.uploadedFilename || row.name,
      original_name: row.name,
      download_url: row.downloadUrl,
      model: projectModel,
    }));
    const idByKey = new Map(toExtract.map((row, i) => [row.key, jobs[i].document_id]));
    jobs.forEach((j) => applied.current.delete(j.document_id));
    try {
      await queue.enqueue(jobs, projectId, { customFields });
      setRows(all.map((r) => (idByKey.has(r.key) ? { ...r, extractionId: idByKey.get(r.key), enqueueFailed: false } : r)));
      const running = all
        .filter((r) => r.extractionId && !idByKey.has(r.key) && !isSettled(statusOf(r)))
        .map((r) => r.extractionId as string);
      queue.startPolling([...running, ...jobs.map((j) => j.document_id)]);
    } catch {
      setRows(all.map((r) => (idByKey.has(r.key) ? { ...r, enqueueFailed: true } : r)));
    }
  };

  // ── Step 1: pick and upload ──

  const addFiles = (files: File[]) => {
    const added = files.map(newRow);
    setRows((prev) => [...prev, ...added]);
  };

  const handleUpload = async () => {
    const pending = rows.filter((r) => r.file);
    if (pending.length === 0) return;
    setError(null);
    upload.clear();
    await upload.upload(pending.map((r) => r.file as File));
  };

  onUploadSettled.current = (items) => {
    upload.clear();
    const byFile = new Map(items.map((item) => [item.file, item]));
    const landed: UploadRow[] = [];
    const failed: string[] = [];
    rows.forEach((row) => {
      const item = row.file ? byFile.get(row.file) : undefined;
      const result = item?.status === "success" ? item.result?.[0] : undefined;
      if (result) landed.push({ ...row, uploadedFilename: result.filename, downloadUrl: result.download_url });
      else failed.push(item?.error ? `${row.name} (${item.error})` : row.name);
    });
    if (failed.length) setError(`Couldn't upload ${failed.join(", ")}`);
    if (landed.length === 0) return;
    setRows(landed);
    setUploaded(true);
    setExpandedKey(landed[0].key);
    void enqueueExtraction(landed, landed);
  };

  const cancel = async () => {
    if (uploaded) await queue.cancelPending(projectId).catch(() => 0);
    queue.reset();
    applied.current.clear();
    setRows([]);
    setUploaded(false);
    setExpandedKey(null);
    setError(null);
    onCancel?.();
  };

  // ── Step 2: save, re-key, index, link (FRaiME's order) ──

  const settled = rows.length > 0 && rows.every((r) => isSettled(statusOf(r)));

  const handleSubmit = async () => {
    if (!settled || submitStage !== "idle") return;
    setError(null);
    const entries = rows.map((row) => ({ row, values: forms.current.get(row.key)?.getValues() ?? blankValues(customFields) }));

    // One at a time, so each created record lines up with its row.
    setSubmitStage("saving");
    const payloads = entries.map(({ row, values }) => toCreatePayload(row, values));
    const { created, failed } = await createDocuments(payloads, { concurrency: 1 });
    const failedPayloads = new Set(failed.map((f) => f.payload));
    const saved = entries.filter((_, i) => !failedPayloads.has(payloads[i])).map((entry, i) => ({ ...entry, doc: created[i] }));

    if (saved.length > 0) {
      // Best-effort: the document is saved either way and can be re-extracted later.
      await Promise.all(
        saved.map(({ row, doc }) => (row.extractionId ? queue.rekey(row.extractionId, doc.id, projectId).catch(() => undefined) : undefined)),
      );
      // As in FRaiME: hand the documents to the indexer here; the indexing itself runs in the background.
      setSubmitStage("indexing");
      try {
        // No rag_mode: with a project, the service applies the project's own RAG mode.
        await indexing.enqueue(
          saved.map(({ doc, row }) => ({ document_id: doc.id, filename: doc.filename ?? row.uploadedFilename, doc_action: "ingest" })),
          projectId,
        );
      } catch (e) {
        setError(`Indexing failed: ${e instanceof Error ? e.message : "unknown error"}`);
        setSubmitStage("idle");
        return;
      }

      setSubmitStage("linking");
      const customFieldValues: Record<string, Record<string, unknown>> = {};
      saved.forEach(({ doc, values }) => {
        const filled = filledCustomValues(values.custom);
        if (Object.keys(filled).length) customFieldValues[doc.id] = filled;
      });
      // Documents are saved and indexed; carry on even if linking fails.
      await linkDocuments(projectId, saved.map(({ doc }) => doc.id), { customFieldValues }).catch(() => undefined);
    }

    setSubmitStage("idle");
    if (failed.length > 0) {
      // Keep only the rows that didn't save, so the user can fix and retry them.
      const savedKeys = new Set(saved.map(({ row }) => row.key));
      setRows((prev) => prev.filter((r) => !savedKeys.has(r.key)));
      setError(`Couldn't save ${failed.map((f) => `${f.payload.filename} (${f.error})`).join("; ")}`);
      return;
    }
    queue.reset();
    applied.current.clear();
    setRows([]);
    setUploaded(false);
    setExpandedKey(null);
    onComplete();
  };

  // ── Render ──

  const extractedCount = rows.filter((r) => isSettled(statusOf(r))).length;
  const busy = upload.isUploading || submitStage !== "idle";
  const idleLabel = queue.isPolling ? "Extracting metadata..." : "Save documents";
  const submitLabel = { idle: idleLabel, saving: "Saving...", indexing: "Indexing...", linking: "Adding to project..." }[submitStage];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-foreground">Upload documents</h2>
        <p className="text-sm text-muted-foreground">
          Metadata is extracted automatically; review it, then save to index the documents into this project&apos;s evidence base.
        </p>
      </div>

      {!uploaded && (
        <FileDropzone
          multiple
          accept={ACCEPTED_FILE_TYPES}
          validate={(file) =>
            isAllowedDocumentFile(file.name) ? { valid: true } : { valid: false, message: `${file.name}: only PDF, DOCX and PPTX are supported` }
          }
          onFilesAdmitted={addFiles}
          onRejected={setRejections}
          isBusy={upload.isUploading}
          label="Drag & drop your files here"
          hint="PDF, DOCX or PPTX up to 50MB"
          browseLabel="Browse files"
        />
      )}

      {rejections.length > 0 && (
        <p className="text-sm text-destructive">
          Skipped {rejections.length} file{rejections.length === 1 ? "" : "s"}: {rejections.map((r) => r.file.name).join(", ")}
        </p>
      )}
      {error && <ErrorBanner message={error} />}

      {upload.isUploading && <UploadQueue items={toQueueItems(upload.items)} overallProgress={upload.overallProgress} onCancel={upload.cancel} />}

      {uploaded && queue.isPolling && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span className="flex-1">
            Extracting metadata {extractedCount} of {rows.length}. Fields fill in when each one is done.
          </span>
        </div>
      )}

      {rows.length > 0 && !upload.isUploading && (
        <div className="space-y-3">
          {rows.map((row) => {
            const isExpanded = expandedKey === row.key;
            const status = uploaded ? statusOf(row) : undefined;
            const queuePosition = row.extractionId ? queue.documentStatuses[row.extractionId]?.queue_position : undefined;
            return (
              <div key={row.key} className="overflow-hidden rounded-lg border bg-card">
                <div className="flex items-center justify-between px-4 py-3">
                  <button
                    type="button"
                    className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground hover:text-brand-teal disabled:hover:text-foreground"
                    onClick={() => setExpandedKey(isExpanded ? null : row.key)}
                    disabled={!uploaded}
                  >
                    {uploaded && (isExpanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />)}
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{row.name}</span>
                    {row.size > 0 && <span className="shrink-0 font-normal text-muted-foreground">({(row.size / 1024).toFixed(1)} KB)</span>}
                  </button>
                  <div className="ml-2 flex shrink-0 items-center gap-2 text-xs">
                    {duplicates.has(row.key) && (
                      <span className="flex items-center gap-1 text-amber-600" title="A document with this file name is already in the project">
                        <AlertTriangle className="h-3.5 w-3.5" /> Already in project
                      </span>
                    )}
                    {status === "queued" && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" /> Queued{queuePosition ? ` (${queuePosition} of ${queue.queueSize})` : ""}
                      </span>
                    )}
                    {status === "processing" && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Extracting...
                      </span>
                    )}
                    {status === "done" && (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="h-3.5 w-3.5" /> AI extracted
                      </span>
                    )}
                    {status === "failed" && (
                      <>
                        <span className="flex items-center gap-1 text-amber-600">
                          <AlertCircle className="h-3.5 w-3.5" /> Extraction failed
                        </span>
                        <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={busy} onClick={() => void enqueueExtraction([row], rows)}>
                          <RefreshCw className="h-3 w-3" /> Re-extract
                        </Button>
                      </>
                    )}
                    {!uploaded && (
                      <button
                        type="button"
                        title="Remove file"
                        aria-label={`Remove ${row.name}`}
                        onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                        className="rounded-full p-1 hover:bg-muted"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Mounted for every uploaded row so values survive collapsing; hidden unless expanded. */}
                {uploaded && (
                  <div className={isExpanded ? "border-t p-4" : "hidden"}>
                    <RowForm row={row} customFields={customFields} register={registerForm} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end gap-3">
        {(rows.length > 0 || onCancel) && (
          <Button type="button" variant="outline" disabled={busy} onClick={() => void cancel()}>
            Cancel
          </Button>
        )}
        {!uploaded ? (
          <Button type="button" variant="brand" className="gap-1.5" disabled={rows.length === 0 || upload.isUploading} onClick={() => void handleUpload()}>
            <Upload className="h-4 w-4" /> Upload
          </Button>
        ) : (
          <Button type="button" variant="brand" className="gap-1.5" disabled={!settled || busy} onClick={() => void handleSubmit()}>
            {queue.isPolling || busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DocumentUploadPanel;
